import type { LevelOneProgressItem } from '@lemonade-hq/uniclient-chat';
import { Flex } from '@lemonade-hq/uniclient-theme';
import { clsx } from 'clsx';
import { motion } from 'motion/react';
import { getAnimationState, getInnerDotAnimationState, getSubItemDotAnimationState } from '../animationHelpers';
import { innerDotVariants, outerDotVariants, subItemDotVariants } from '../animations';
import type { ActionProps, AnimationProgressProps, BaseProgressItemProps, SubItemDotProps } from '../types';
import { AnimatedCheckmark, AnimatedExclamationMark } from './AnimatedIcons';
import * as styles from './Dots.css';

export const InnerDot: React.FC<BaseProgressItemProps> = ({ completed, current }) => (
  <motion.div
    animate={getInnerDotAnimationState(completed, current)}
    className={styles.innerDot}
    initial={false}
    variants={innerDotVariants}
  />
);

interface OuterDotProps extends BaseProgressItemProps, ActionProps, Pick<AnimationProgressProps, 'isBackwards'> {
  readonly status: LevelOneProgressItem['status'];
  readonly disabled?: boolean;
}

export const OuterDot: React.FC<OuterDotProps> = ({
  completed,
  current,
  status,
  onAction,
  action,
  isBackwards,
  disabled,
}) => (
  <Flex
    alignItems="center"
    animate={getAnimationState(completed, current)}
    as={motion.div}
    className={completed && status !== 'notice' ? clsx(styles.dot, styles.dotCompleted) : styles.dot}
    cursor={action != null && !disabled ? 'pointer' : 'default'}
    custom={{ isBackwards }}
    initial={false}
    justifyContent="center"
    onClick={() => action != null && onAction != null && !disabled && void onAction(action)}
    variants={outerDotVariants}
  >
    {status !== 'notice' ? <InnerDot completed={completed} current={current} /> : <AnimatedExclamationMark />}
    {completed && (
      <Flex position="absolute">{status !== 'notice' ? <AnimatedCheckmark /> : <AnimatedExclamationMark />}</Flex>
    )}
  </Flex>
);

export const SubItemDot: React.FC<BaseProgressItemProps & SubItemDotProps> = ({
  current,
  completed,
  isBackwards,
  isFirst,
  previousSubItemHasSubSubItems,
}) => (
  <motion.div
    animate={getSubItemDotAnimationState(completed, current)}
    className={styles.subItemDot}
    initial="initial"
    variants={subItemDotVariants({ previousSubItemHasSubSubItems, isFirst, isBackwards })}
  />
);
